/**
 * DesignSystemPreview — curated live block preview.
 *
 * Loads a small representative set of real card blocks via loadBlock(),
 * assembles an AppV2 manifest, and pushes
 * it through CogoIframe.loadApp. The themeTokens prop (built from the 6-axis
 * useDesignSystem hook) is forwarded into the iframe so changing any axis
 * re-renders the grid in real time.
 *
 * No more static preview-01.json / preview-02.json payloads — those have been
 * deleted per Phase 365 D-08.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { LoadAppPayload } from '@cogo/iframe-protocol'
import type { BlockDef, App, Page } from '@cogo/types-v2'
import { axesToThemeV2, resolveTheme, type DesignSystemAxes } from '@cogo/design-presets'
import { CogoIframe, type CogoIframeRef } from '@/components/cogo-iframe'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'
import {
  loadBlock,
  type LoadedBlock,
} from '@/features/shared/services/block-loader'
import { computeThemeTokens } from '../hooks/use-design-system'
import type { DesignSystemState } from '../lib/design-system-options'
import { UiBlockPreviewAuthorityError, type UiBlockRef } from '../ui-blocks'
import { readAdmittedUiBlockPreview } from '../design-system-consumer'
import { getDesignSystemPreviewCopy } from './design-system-preview-copy'

// ---------------------------------------------------------------------------
// Representative product surfaces. The former 31-card, 3000px-wide catalogue
// made the first screen look like an internal component manual. These six
// blocks still exercise typography, controls, charts, forms, and status while
// keeping the live result readable inside the workspace.
// ---------------------------------------------------------------------------

const COLUMNS: string[][] = [
  ['card-style-overview', 'card-ui-elements'],
  ['card-typography-specimen', 'card-bar-chart'],
  ['card-feedback-form', 'card-analytics'],
]

const ALL_BLOCK_IDS = COLUMNS.flat()

const DEFAULT_BLOCK_REFS: readonly UiBlockRef[] = ALL_BLOCK_IDS.map((blockId) => ({
  category: 'card',
  blockId,
}))

const PREVIEW_READY_TIMEOUT_MS = 8_000
const PREVIEW_RENDER_PROBE_MS = 100
const PREVIEW_APP_ID = 'design-system-preview'

export type DesignSystemPreviewStatus =
  | 'loading'
  | 'waiting'
  | 'ready'
  | 'error'
  | 'timeout'

function normalizeToBlockDef(loaded: LoadedBlock): BlockDef {
  return {
    meta: (loaded.manifest ?? {}) as unknown as BlockDef['meta'],
    ui: (loaded.ui ?? {}) as unknown as BlockDef['ui'],
    events: (loaded.events ?? {}) as unknown as BlockDef['events'],
    flow: (loaded.flow ?? {}) as unknown as BlockDef['flow'],
    state: (loaded.variables ?? {}) as unknown as BlockDef['state'],
    data: (loaded.datasource ?? {}) as unknown as BlockDef['data'],
  }
}

function buildManifest(previewBlockRefs?: readonly UiBlockRef[]): App {
  const selectedRefs = previewBlockRefs?.length ? previewBlockRefs : null
  const columns = selectedRefs
    ? [selectedRefs.map((block) => block.blockId)]
    : COLUMNS
  const blockIds = selectedRefs?.map((block) => block.blockId) ?? ALL_BLOCK_IDS
  const gridChildren = columns.map((colBlocks, colIdx) => ({
    id: `col-${colIdx}`,
    type: 'container',
    className: 'flex flex-col gap-[40px] p-1',
    children: colBlocks.map((blockId) => ({
      id: `ref-${blockId}`,
      type: 'block-ref',
      blockType: blockId,
    })),
  }))

  const page: Page = {
    id: 'grid',
    route: '/',
    title: 'Design System Preview',
    blocks: blockIds,
    blockLayout: {},
    ui: {
      root: {
        id: 'grid-root',
        type: 'container',
        className: selectedRefs
          ? 'grid min-h-full w-full grid-cols-1 items-start gap-[24px] bg-muted p-[24px]'
          : 'grid w-full grid-cols-3 items-start gap-[24px] bg-muted p-[24px]',
        children: gridChildren,
      },
    },
  } as any

  return {
    appId: PREVIEW_APP_ID,
    name: 'Design System Preview',
    version: '1.0',
    schemaVersion: '3.0',
    pages: [page],
    globalState: {},
    appData: {},
    datasources: {},
  }
}

// ---------------------------------------------------------------------------
// Pure payload composer (Phase M1 Task 8)
// ---------------------------------------------------------------------------

/**
 * Compose the IFRAME:LOAD_APP payload from 6-axis state + the loaded blocks
 * dict. Carries both the legacy themeTokens (for /block-grid-test BC) AND
 * the new resolved theme v2 (cssVars + atomProps), keyed off the same state
 * so the two paths can never diverge mid-frame.
 *
 * Top-level export so it can be unit-tested in isolation
 * (`__tests__/preview-payload.test.ts`).
 */
export function buildPayload(
  state: DesignSystemState,
  blocks: Record<string, BlockDef>,
  previewBlockRefs?: readonly UiBlockRef[],
  designSystem?: Readonly<Record<string, string>>,
): LoadAppPayload {
  const themeV2 = axesToThemeV2(state as DesignSystemAxes)
  const resolved = resolveTheme(themeV2)
  const legacy = computeThemeTokens(state)

  return {
    manifest: buildManifest(previewBlockRefs) as unknown as Record<string, unknown>,
    blocks: blocks as unknown as Record<string, unknown>,
    ...(designSystem ? { designSystem: { ...designSystem } } : {}),
    themeTokens: legacy as unknown as Record<string, string>,
    theme: resolved,
    styleName: state.style,
    fontName: state.font,
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface DesignSystemPreviewProps {
  /**
   * Phase M1 Task 8: pass the full 6-axis state. The component derives both
   * legacy themeTokens AND resolved theme v2 internally via the top-level
   * `buildPayload()` pure function, ensuring the two paths share one source
   * of truth.
   */
  state: DesignSystemState
  className?: string
  /** Omit to retain the curated six-block default preview. */
  previewBlockRefs?: readonly UiBlockRef[]
}

export function DesignSystemPreview({
  state,
  className,
  previewBlockRefs,
}: DesignSystemPreviewProps) {
  const { locale } = useStudioLocale()
  const copy = getDesignSystemPreviewCopy(locale)
  const iframeRef = useRef<CogoIframeRef>(null)
  const [blocks, setBlocks] = useState<Record<string, BlockDef> | null>(null)
  const [designSystem, setDesignSystem] = useState<Readonly<Record<string, string>> | null>(null)
  const [status, setStatus] = useState<DesignSystemPreviewStatus>('loading')
  const [authorityFailure, setAuthorityFailure] = useState<UiBlockPreviewAuthorityError | null>(null)
  const [attempt, setAttempt] = useState(0)

  const previewBlockRefsKey = previewBlockRefs?.length
    ? previewBlockRefs.map((block) => `${block.category}/${block.blockId}`).join('|')
    : ''
  const activeBlockRefs = useMemo<readonly UiBlockRef[]>(() => {
    if (!previewBlockRefsKey) return DEFAULT_BLOCK_REFS
    return previewBlockRefsKey.split('|').map((serializedRef) => {
      const separator = serializedRef.indexOf('/')
      return {
        category: serializedRef.slice(0, separator),
        blockId: serializedRef.slice(separator + 1),
      }
    })
  }, [previewBlockRefsKey])
  const manifestPreviewBlockRefs = previewBlockRefsKey ? activeBlockRefs : undefined
  const verifyCatalogAdmission = previewBlockRefsKey.length > 0

  // Load the curated defaults, or the selected catalog block, without
  // writing anything back to project state.
  useEffect(() => {
    let cancelled = false
    async function loadAll() {
      setStatus('loading')
      setAuthorityFailure(null)
      setBlocks(null)
      setDesignSystem(null)
      try {
        const entries: [string, BlockDef][] = []
        const designRoleEntries: Array<[string, string]> = []
        for (const { category, blockId } of activeBlockRefs) {
          const resolvedPreview = verifyCatalogAdmission
            ? await readAdmittedUiBlockPreview({ category, blockId })
            : null
          const loaded = resolvedPreview?.block ?? await loadBlock(category, blockId)
          if (Object.keys(loaded.manifest).length === 0 || Object.keys(loaded.ui).length === 0) {
            throw new Error(`Preview unavailable for ${category}/${blockId}`)
          }
          if (resolvedPreview) {
            designRoleEntries.push(...Object.entries(resolvedPreview.designSystem))
          }
          const cloned = JSON.parse(JSON.stringify(loaded)) as LoadedBlock
          entries.push([blockId, normalizeToBlockDef(cloned)])
        }
        if (!cancelled) {
          setDesignSystem(
            verifyCatalogAdmission ? Object.fromEntries(designRoleEntries) : {},
          )
          setBlocks(Object.fromEntries(entries))
          setStatus('waiting')
        }
      } catch (error) {
        if (!cancelled) {
          setAuthorityFailure(
            error instanceof UiBlockPreviewAuthorityError ? error : null,
          )
          setStatus('error')
        }
      }
    }
    loadAll()
    return () => {
      cancelled = true
    }
  }, [activeBlockRefs, verifyCatalogAdmission, attempt])

  const composePayload = useCallback((): LoadAppPayload | null => {
    if (!blocks) return null
    if (verifyCatalogAdmission && !designSystem) return null
    return buildPayload(
      state,
      blocks,
      manifestPreviewBlockRefs,
      verifyCatalogAdmission ? designSystem ?? undefined : undefined,
    )
  }, [state, blocks, manifestPreviewBlockRefs, verifyCatalogAdmission, designSystem])

  // On iframe ready: push the current payload (if blocks loaded).
  const handleIframeReady = useCallback(() => {
    const payload = composePayload()
    if (payload) iframeRef.current?.loadApp(payload)
  }, [composePayload])

  // Re-push whenever blocks finish loading or state changes.
  useEffect(() => {
    const payload = composePayload()
    if (payload) iframeRef.current?.loadApp(payload)
  }, [composePayload])

  // The legacy route's iframe `load` event is not proof that AppV2 rendered:
  // DGX evidence showed a successful document load with an empty body. Keep
  // the skeleton up until the exact parent preview's sandbox render root has
  // content, and fail visibly if that never happens.
  useEffect(() => {
    if (status !== 'waiting' || !blocks) return
    const deadline = window.setTimeout(() => {
      setStatus((current) => current === 'waiting' ? 'timeout' : current)
    }, PREVIEW_READY_TIMEOUT_MS)
    const probe = window.setInterval(() => {
      try {
        const document = iframeRef.current?.getIframeElement()?.contentDocument
        const renderRoot = document?.querySelector<HTMLElement>(
          `[data-cogo-app-render-root="true"][data-app-id="${PREVIEW_APP_ID}"]`,
        )
        if (renderRoot && (renderRoot.childElementCount > 0 || Boolean(renderRoot.textContent?.trim()))) {
          setStatus('ready')
        }
      } catch {
        // A blocked or unexpectedly cross-origin route cannot prove readiness;
        // the deadline below converts it into an explicit timeout state.
      }
    }, PREVIEW_RENDER_PROBE_MS)
    return () => {
      window.clearTimeout(deadline)
      window.clearInterval(probe)
    }
  }, [status, blocks, attempt, previewBlockRefsKey])

  const retry = useCallback(() => {
    setStatus('loading')
    setAttempt((current) => current + 1)
  }, [])

  if (status === 'error' || status === 'timeout') {
    const authorityMessage = authorityFailure?.code === 'not-admitted'
      ? copy.catalogRejected.replace(
        '{ref}',
        `${authorityFailure.ref.category}/${authorityFailure.ref.blockId}`,
      )
      : authorityFailure?.code === 'design-authority-unresolved'
        ? copy.ontologyRejected.replace(
          '{roles}',
          authorityFailure.roles.map((role) => `@ds:${role}`).join(', '),
        )
        : null
    return (
      <div
        role="alert"
        data-testid="design-system-preview-state"
        data-preview-status={status}
        className={cn(
          'flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-destructive',
          className,
        )}
      >
        <p className="text-sm font-medium">
          {status === 'timeout' ? copy.timeout : authorityMessage ?? copy.error}
        </p>
        {status === 'timeout' || !authorityFailure ? (
          <Button type="button" variant="outline" onClick={retry}>
            {copy.retry}
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className={cn('relative flex h-full flex-col', className)}>
      <CogoIframe
        key={`${previewBlockRefsKey}:${attempt}`}
        ref={iframeRef}
        src="/block-grid-test-legacy"
        className={cn(
          'h-full w-full flex-1 transition-opacity duration-200',
          status !== 'ready' && 'pointer-events-none opacity-0',
        )}
        onReady={handleIframeReady}
      />
      {status !== 'ready' && (
        <div
          data-testid="design-system-preview-state"
          data-preview-status={status}
          className="absolute inset-0 grid grid-cols-2 gap-4 overflow-hidden bg-muted/30 p-5 lg:grid-cols-4"
          aria-label="Loading design preview"
          aria-live="polite"
        >
          <p className="absolute left-5 top-3 z-10 rounded-full border border-border bg-background/95 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm">
            {status === 'loading' ? copy.loading : copy.waiting}
          </p>
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={index}
              className="min-h-40 rounded-lg border border-border bg-card p-4 shadow-sm"
            >
              <div className="h-3 w-24 rounded-full bg-muted" />
              <div className="mt-4 h-8 w-2/3 rounded-md bg-muted" />
              <div className="mt-4 space-y-2">
                <div className="h-2 rounded-full bg-muted" />
                <div className="h-2 w-5/6 rounded-full bg-muted" />
                <div className="h-2 w-1/2 rounded-full bg-muted" />
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2">
                <div className="h-9 rounded-md border border-border bg-background" />
                <div className="h-9 rounded-md border border-border bg-background" />
                <div className="h-9 rounded-md border border-border bg-background" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
